import {
  AppDataComponent,
  DocumentServicesObject,
  EditorClientSpecMapEntry,
  UnifiedWidget,
  WidgetPluginComponent,
} from '@wix/document-services-types';
import {
  Breakpoint,
  Item,
  Section,
  SubGroup,
  SubGroupItem,
} from '@wix/editor-platform-host-integration-apis';
import {
  ComponentType,
  Preset,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../../types/platformApi';
import {
  ALLOWED_EXTENSIONS,
  BUILDER_BASED_ALLOWED_EXTENSIONS,
} from './constants';
import {
  AppDataComponentWithItems,
  AppDataComponentWithSubGroupItems,
  PlatformAppCategoryDefinition,
  PlatformAppItemDefinition,
  PlatformAppWithCategoryDefinition,
} from './types';
import { AppData } from '../../../types/unifiedComponents';
import {
  mapBuilderPresetToSubGroupItem,
  mapDefinitionToItem,
  mapDefinitionToSection,
  mapDefinitionToSubGroup,
} from './mappers';
import {
  type BuilderPreset,
  builderPresetsMapper,
  isBuilderComponent,
  sortPresets,
} from './builderUtils';
import { getCurrentBreakpoint } from '../../utils/getCurrentBreakpoint';
import {
  getPageDataByComponentId,
  isComponentEssential,
} from '../unifiedComponents/componentSettings';

export const isValidUnifiedPreset = (
  preset?: Partial<Preset>,
): preset is Preset =>
  Boolean(preset && preset.id && preset.name && preset.thumbnailUrl);

export const isValidBuilderPreset = (
  preset?: Partial<BuilderPreset>,
): preset is BuilderPreset =>
  Boolean(preset && preset.id && preset.displayName && preset.thumbnailUrl);

export const isValidBlocksPresetSize = (
  size?: Partial<PlatformAppItemDefinition['size']>,
): size is PlatformAppItemDefinition['size'] =>
  Boolean(size && size.width && size.height);

export const isValidBlocksPreset = (
  preset?: PlatformAppCategoryDefinition['items'][number],
): preset is PlatformAppItemDefinition =>
  Boolean(
    preset &&
      preset.widgetId &&
      preset.presetId &&
      preset.desc &&
      preset.image &&
      isValidBlocksPresetSize(preset.size),
  );

export const isValidPreset = (
  preset?: Partial<Preset> | PlatformAppCategoryDefinition['items'][number],
): preset is Preset | PlatformAppItemDefinition =>
  isValidUnifiedPreset(preset) || isValidBlocksPreset(preset);

const getBuilderPresetsToShowInAddPanel = (
  componentType: string,
  breakpoint: Breakpoint,
  context: PlatformContext,
): BuilderPreset[] => {
  const presets =
    context.componentsPlatformService.manifest.getPresetsByComponentType(
      componentType,
      {
        breakpoint,
      },
    );
  return builderPresetsMapper(presets ?? {}).filter(isValidBuilderPreset);
};

export const getBuilderItems = (
  componentType: string,
  documentServices: DocumentServicesObject,
  context: PlatformContext,
): SubGroupItem[] => {
  const currentBreakpoint = getCurrentBreakpoint(documentServices, context);
  const presets = getBuilderPresetsToShowInAddPanel(
    componentType,
    currentBreakpoint,
    context,
  );

  return sortPresets(presets, componentType, currentBreakpoint, context).map(
    mapBuilderPresetToSubGroupItem,
  );
};

export const getPresetsToShowInAddPanel = (
  component: AppDataComponent,
  addPanelDefinitionForBlocks?: PlatformAppCategoryDefinition,
): Preset[] | PlatformAppItemDefinition[] =>
  (
    (component.data as UnifiedWidget)?.presets ??
    addPanelDefinitionForBlocks?.items?.filter(
      (item) => item.widgetId === component.componentId,
    ) ??
    []
  ).filter(isValidPreset);

export const getItems = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  appData: AppData,
  component: AppDataComponent,
  addPanelDefinitionForBlocks?: PlatformAppCategoryDefinition,
): Item[] => {
  const presets = getPresetsToShowInAddPanel(
    component,
    addPanelDefinitionForBlocks,
  );

  return presets.map((preset) =>
    mapDefinitionToItem(
      documentServices,
      context,
      appData,
      component,
      preset,
      presets,
    ),
  );
};

export const isExtensionAllowed = (
  compType: ComponentType,
  allowedExtensions: ComponentType[],
) => Boolean(compType && allowedExtensions.includes(compType));

export const getPlugins = (appData: AppData): string[] =>
  appData.components
    .filter(
      (component) =>
        component.type === ComponentType.WIDGET_PLUGIN && component.data,
    )
    .map(
      (component) =>
        (component as WidgetPluginComponent).data.referenceComponentId,
    );

export const shouldBeAdded = (
  context: PlatformContext,
  appData: AppData,
  { type, componentId, data }: AppDataComponent = {},
  allowedExtensions: ComponentType[] = ALLOWED_EXTENSIONS,
): boolean => {
  if (!type || !componentId || !data) {
    return false;
  }

  if (!isExtensionAllowed(type as ComponentType, allowedExtensions)) {
    return false;
  }

  const plugins = getPlugins(appData);

  if (plugins.includes(componentId)) {
    return false;
  }

  const pageData = getPageDataByComponentId(appData, componentId);
  const tpaPageId = pageData?.base?.id;

  const shouldReadDataFromNewSchema = context.experimentsAPI.enabled(
    'specs.editor-platform.unified-components-proto-changes',
  );

  return !isComponentEssential(
    componentId,
    appData,
    shouldReadDataFromNewSchema,
    tpaPageId,
  );
};

export const getAddPanelDefinitionForBlocks = (
  documentServices: DocumentServicesObject,
  appId: string,
): PlatformAppCategoryDefinition | undefined => {
  const appManifest: PlatformAppWithCategoryDefinition =
    documentServices.platform.getAppManifest(appId);
  return appManifest?.addPanelSectionDefinition;
};

export const getComponentsByOrder = (
  appData: AppData,
  addPanelDefinitionForBlocks: PlatformAppCategoryDefinition | undefined,
): AppDataComponent[] => {
  if (!addPanelDefinitionForBlocks) {
    return appData.components;
  }

  const addedBlocksComponentIds = new Set<string>();

  const orderedBlocksComponents = (
    addPanelDefinitionForBlocks.items ?? []
  ).reduce<AppDataComponent[]>((uniqueComponents, blocksWidget) => {
    if (
      blocksWidget.widgetId &&
      !addedBlocksComponentIds.has(blocksWidget.widgetId)
    ) {
      const component = appData.components.find(
        (comp) => comp.componentId === blocksWidget.widgetId,
      );

      if (component) {
        uniqueComponents.push(component);
        addedBlocksComponentIds.add(blocksWidget.widgetId);
      }
    }

    return uniqueComponents;
  }, []);

  const remainingComponents = appData.components.filter(
    (component) => !addedBlocksComponentIds.has(component.componentId ?? ''),
  );

  return [...orderedBlocksComponents, ...remainingComponents];
};

const getDefaultSubGroupItem = (
  componentType: string,
  context: PlatformContext,
): SubGroupItem => ({
  id: undefined,
  title:
    context.componentsPlatformService.manifest.getDisplayNameByComponentType(
      componentType,
    ) ?? '',
  image: undefined,
});

export const getSubGroups = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  appDefinitionId: string,
): SubGroup[] => {
  const appData = documentServices.platform.getAppDataByAppDefId(
    appDefinitionId,
  ) as Required<AppData>;
  if (!appData?.components?.length) {
    return [];
  }

  return appData.components
    .filter((component: AppDataComponent) => {
      return shouldBeAdded(
        context,
        appData,
        component,
        BUILDER_BASED_ALLOWED_EXTENSIONS,
      );
    })
    .map((component: AppDataComponent) => {
      const componentType = isBuilderComponent(component)
        ? component.data.type!
        : component.componentId!;
      const items = getBuilderItems(componentType, documentServices, context);
      return {
        component,
        items:
          items.length > 0
            ? items
            : [getDefaultSubGroupItem(componentType, context)],
        type: componentType,
      };
    })
    .map((componentWithItems: AppDataComponentWithSubGroupItems, index) =>
      mapDefinitionToSubGroup(componentWithItems, index, context),
    );
};

export const getSections = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  app: EditorClientSpecMapEntry,
): Section[] => {
  const appData = documentServices.platform.getAppDataByAppDefId(
    app.appDefinitionId,
  ) as Required<AppData>;
  if (!appData?.components?.length) {
    return [];
  }

  const addPanelDefinitionForBlocks = getAddPanelDefinitionForBlocks(
    documentServices,
    app.appDefinitionId,
  );
  const components = getComponentsByOrder(appData, addPanelDefinitionForBlocks);

  return components
    .filter((component: AppDataComponent) =>
      shouldBeAdded(context, appData, component),
    )
    .map((component: AppDataComponent) => ({
      component,
      items: getItems(
        documentServices,
        context,
        appData,
        component,
        addPanelDefinitionForBlocks,
      ),
    }))
    .filter(
      (componentWithItems: AppDataComponentWithItems) =>
        componentWithItems.items.length,
    )
    .map((componentWithItems: AppDataComponentWithItems, index) =>
      mapDefinitionToSection(componentWithItems, index),
    );
};
